import { normalize } from 'normalizr'
import { ActionContext, Commit } from 'vuex'

import Booking, { RawBookingResponse } from '@/models/booking'
import BookingStatusBarInterface from '@/models/booking-status-bar'
import Payment from '@/models/payment'

import axios from '@/axios-module'
import notification from '@/notification'
import router from '@/router'
import { BookingsResponse, bookingsSchema } from '@/schemas/booking'
import { CreditNotesReponse } from '@/schemas/creditNote'
import deepmerge from '@/services/deepmerge/deepmerge'
import { PaymentMutationTypes } from '@/store/modules/payments'

import { CreditNoteMutationTypes } from './creditNotes'

export enum BookingsActionTypes {
  Load = '[Bookings] Load',
  Get = '[Bookings] Get',
  TrackCheckout = '[Bookings] TrackCheckout',
  GetStatusBar = '[Bookings] GetStatusBar',
}

export enum BookingsMutationTypes {
  LoadSuccess = '[Bookings] LoadSuccess',
  GetStarted = '[Bookings] GetStarted',
  GetSuccess = '[Bookings] GetSuccess',
  GetFailure = '[Bookings] GetFailure',
  GetStatusBarSuccess = '[Bookings] GetStatusBarSuccess',
  ScopeChange = '[Bookings] ScopeChange',
  ResetStatusBar = '[Bookings] ResetStatusBar',
  UpdatePaidCents = '[Bookings] UpdatePaidCents',
  Reset = '[Bookings] Reset',
}

export interface State {
  bookingsLoaded: boolean
  bookingStatusBarLoaded: boolean
  bookingEntities: {
    [id: string]: Booking
  }
  bookingIdsByScope: {
    [scope: string]: string[]
  }
  bookingsScope: string // should be enum
}

function initialState(): State {
  return {
    bookingsLoaded: false,
    bookingStatusBarLoaded: false,
    bookingEntities: {},
    bookingsScope: 'active',
    bookingIdsByScope: {},
  }
}

export default {
  state: initialState,
  getters: {
    getBookingsByScope(state: State): (scope: string) => Booking[] {
      return (scope: string) =>
        (state.bookingIdsByScope[scope] || []).map(
          (id) => state.bookingEntities[id],
        )
    },
    getBookings(state: State): Booking[] {
      return (state.bookingIdsByScope[state.bookingsScope] || []).map(
        (id) => state.bookingEntities[id],
      )
    },
    getBookingsScope(state: State): string {
      return state.bookingsScope
    },
    getBooking(state: State): (id: string) => Booking {
      return (id: string) => state.bookingEntities[id]
    },
  },
  actions: {
    async [BookingsActionTypes.Load]({ commit }: { commit: Commit }) {
      try {
        commit(BookingsMutationTypes.GetStarted)
        const response = await axios.get('/customer/bookings')
        const bookingsResponse = normalize(response.data, bookingsSchema)
        commit(BookingsMutationTypes.LoadSuccess, bookingsResponse)
      } catch (error) {
        router.push({ name: 'dashboard' })

        if (error.response && error.response.status) {
          notification('Sorry! There was an error loading bookings.', 'error')
        } else {
          throw error
        }
      }
    },
    async [BookingsActionTypes.Get](
      { commit }: { commit: Commit },
      id: string,
    ) {
      try {
        const response = await axios.get(`/customer/bookings/${id}`)
        commit(BookingsMutationTypes.GetSuccess, response.data)
      } catch (error) {
        router.push({ name: 'dashboard' })

        switch (error?.response?.status) {
          case 404:
            notification(
              "Unfortunately we couldn't find such booking.",
              'error',
            )
            break
          default:
            notification(
              'Sorry! There was an error loading the booking.',
              'error',
            )
            throw error
        }
      }
    },
    [BookingsActionTypes.TrackCheckout](
      _: ActionContext<any, any>,
      id: string,
    ) {
      axios.post(`/customer/bookings/${id}/ga_checkout`)
    },
    async [BookingsActionTypes.GetStatusBar](
      { commit }: { commit: Commit },
      id: string,
    ) {
      const params = { enquiry_id: id }
      const response = await axios.get(
        `/customer/bookings/${id}/booking_status_bar`,
        { params },
      )

      commit(BookingsMutationTypes.GetStatusBarSuccess, response.data)
    },
  },
  mutations: {
    [BookingsMutationTypes.LoadSuccess](
      state: State,
      bookingsResponse: BookingsResponse,
    ) {
      const { bookings = {} } = bookingsResponse.entities
      state.bookingEntities = deepmerge(state.bookingEntities, bookings)

      const bookingIdsByScope = Object.values(bookings).reduce(
        (carry: { [scope: string]: string[] }, booking: Booking) => {
          carry[booking.bookingsScope] = [
            ...(carry[booking.bookingsScope] || []),
            booking.id,
          ]
          return carry
        },
        {},
      )
      state.bookingIdsByScope = {
        ...state.bookingIdsByScope,
        ...bookingIdsByScope,
      }

      state.bookingsLoaded = true
    },
    [BookingsMutationTypes.GetSuccess](
      state: State,
      booking: RawBookingResponse,
    ) {
      // TODO: Normalizr!!!
      state.bookingsLoaded = true

      const normalizedBooking = {
        ...state.bookingEntities[booking.id],
        ...booking,
        passengerIds: booking.passengers.map((p) => p.id),
      }

      state.bookingEntities = {
        ...state.bookingEntities,
        [booking.id]: normalizedBooking,
      }

      if (!state.bookingIdsByScope[booking.bookingsScope]) {
        state.bookingIdsByScope[booking.bookingsScope] = [booking.id]
        return
      }

      if (
        state.bookingIdsByScope[booking.bookingsScope].indexOf(booking.id) < 0
      ) {
        state.bookingIdsByScope[booking.bookingsScope] = [
          ...state.bookingIdsByScope[booking.bookingsScope],
          booking.id,
        ]
      }
    },
    [BookingsMutationTypes.GetStarted](state: State) {
      state.bookingsLoaded = false
    },
    [BookingsMutationTypes.GetFailure](state: State) {
      state.bookingsLoaded = true
    },
    [BookingsMutationTypes.ScopeChange](state: State, scope: string) {
      state.bookingsScope = scope
    },
    [BookingsMutationTypes.ResetStatusBar](state: State) {
      state.bookingStatusBarLoaded = false
    },
    [BookingsMutationTypes.UpdatePaidCents](
      state: State,
      payload: { bookingId: string | number; outstandingCents: number },
    ) {
      const id = String(payload.bookingId)
      const existing = state.bookingEntities[id]
      if (!existing || typeof existing.totalCents !== 'number') {
        return
      }
      const paidCents = Math.max(
        existing.totalCents - payload.outstandingCents,
        0,
      )
      state.bookingEntities = {
        ...state.bookingEntities,
        [id]: { ...existing, paidCents },
      }
    },
    [BookingsMutationTypes.Reset](state: State) {
      Object.assign(state, initialState())
    },
    [BookingsMutationTypes.GetStatusBarSuccess](
      state: State,
      statusBar: BookingStatusBarInterface,
    ) {
      const booking = {
        ...state.bookingEntities[statusBar.bookingId],
        bookingStatusBar: statusBar,
      }
      state.bookingEntities = {
        ...state.bookingEntities,
        [statusBar.bookingId]: booking,
      }

      state.bookingStatusBarLoaded = true
    },
    [PaymentMutationTypes.LoadByBookingIdSuccess](
      state: State,
      payload: { bookingId: number; payments: Payment[] },
    ) {
      const booking = {
        ...state.bookingEntities[payload.bookingId],
        paymentIds: payload.payments.map((p) => p.id),
      }

      state.bookingEntities = {
        ...state.bookingEntities,
        [payload.bookingId]: booking,
      }
    },
    [CreditNoteMutationTypes.LoadByBookingIdSuccess](
      state: State,
      creditNotesResponse: CreditNotesReponse,
    ) {
      const booking = {
        ...state.bookingEntities[creditNotesResponse.bookingId],
        creditNoteIds: creditNotesResponse.result,
      }

      state.bookingEntities = {
        ...state.bookingEntities,
        [creditNotesResponse.bookingId]: booking,
      }
    },
  },
}
